import { CourseContentRepository } from "./content_interface";
import {
  Content,
  ContentAnnotation,
  ContentComplement,
  ContentDoubt,
  Question,
  QuestionStatus,
  ScormCheckpoint
} from "../../../presentation/views/content/helpers";
import {Rating, RatingList} from "../../../../domain/entities/rating";
import { wrapErrorHandler } from "../../utils/wrap_error_handler";
import http from "../../http";
import { Instructor } from "../../../../domain/entities/entities";
import rating from "../../store/modules/content/rating";

/** return the path to access the contents resources. */
const contentsPath = (courseID: number, contentID?: number): string => {
  const rootPath = `/api/decola/courses/${courseID}/contents`;
  if (contentID) {
    return `${rootPath}/${contentID}`;
  }
  return rootPath;
};

class CourseContentRepositoryImpl implements CourseContentRepository {
  async getAllContents(courseID: number): Promise<Content[]> {
    return await wrapErrorHandler<Promise<Content[]>>(async () => {
      const { data } = await http.get(contentsPath(courseID));
      return data;
    });
  }

  async getContentByID(courseID: number, contentID: number): Promise<Content> {
    return await wrapErrorHandler<Promise<Content>>(async () => {
      const { data } = await http.get(contentsPath(courseID, contentID));
      return data;
    });
  }

  async initializeContent(courseID: number, contentID: number): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const {data} = await http.post(`${contentsPath(courseID, contentID)}/initialize`);
      return data;
    });
  }

  async getAllComplements(
    courseID: number,
    contentID: number
  ): Promise<ContentComplement[]> {
    return await wrapErrorHandler<Promise<ContentComplement[]>>(async () => {
      const { data } = await http.get(
        `${contentsPath(courseID, contentID)}/complements`
      );
      return data;
    });
  }

  async createRating(
    courseID: number,
    contentID: number,
    rating: Rating
  ): Promise<Rating> {
    return await wrapErrorHandler<Promise<Rating>>(async () => {
      const { data } = await http.post(
        `${contentsPath(courseID, contentID)}/ratings`,
        rating
      );
      return data;
    });
  }

  async getAllRatings(
    courseID: number,
    contentID: number,
    index?: number
  ): Promise<RatingList> {
    return await wrapErrorHandler<Promise<RatingList>>(async () => {
      const { data } = await http.get(
        `${contentsPath(courseID, contentID)}/ratings`,
        {
          params: {
            index: index
          }
        }
      );
      return data;
    });
  }

  async createDoubt(
    courseID: number,
    contentID: number,
    contentDoubt: ContentDoubt
  ): Promise<ContentDoubt> {
    return await wrapErrorHandler<Promise<ContentDoubt>>(async () => {
      const { data } = await http.post(
        `${contentsPath(courseID, contentID)}/doubts`,
        contentDoubt
      );
      return data;
    });
  }

  async getAllDoubts(
    courseID: number,
    contentID: number
  ): Promise<ContentDoubt[]> {
    return await wrapErrorHandler<Promise<ContentDoubt[]>>(async () => {
      const { data } = await http.get(
        `${contentsPath(courseID, contentID)}/doubts`
      );
      return data;
    });
  }

  async getContentInstructors(
    courseID: number,
    contentID: number
  ): Promise<Instructor[]> {
    return await wrapErrorHandler<Promise<Instructor[]>>(async () => {
      const { data } = await http.get(
        `${contentsPath(courseID, contentID)}/instructors`
      );
      return data;
    });
  }

  async createContentAnnotation(
    courseID: number,
    contentID: number,
    annotation: ContentAnnotation
  ): Promise<ContentAnnotation> {
    return await wrapErrorHandler<Promise<ContentAnnotation>>(async () => {
      const { data } = await http.post(
        `${contentsPath(courseID, contentID)}/notes`,
        annotation
      );
      return data;
    });
  }

  async getAllContentAnnotations(
    courseID: number,
    contentID: number
  ): Promise<ContentAnnotation[]> {
    return await wrapErrorHandler<Promise<ContentAnnotation[]>>(async () => {
      const { data } = await http.get(
        `${contentsPath(courseID, contentID)}/notes`
      );
      return data;
    });
  }

  async updateContentAnnotation(
    courseID: number,
    contentID: number,
    annotation: ContentAnnotation
  ): Promise<ContentAnnotation> {
    return await wrapErrorHandler<Promise<ContentAnnotation>>(async () => {
      const { data } = await http.post(
        `${contentsPath(courseID, contentID)}/notes/${annotation.id}`,
        annotation
      );
      return data;
    });
  }

  async deleteContentAnnotation(
    courseID: number,
    contentID: number,
    annotationID: number
  ): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const { data } = await http.delete(
        `${contentsPath(courseID, contentID)}/notes/${annotationID}`
      );
      return data;
    });
  }

  async saveCheckpointSeconds(
    courseID: number,
    contentID: number,
    timestamp: number
  ): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const { data } = await http.post(
        `${contentsPath(courseID, contentID)}/checkpoints`,
        timestamp
      );
      return data;
    });
  }

  async saveCheckpointSCORM(
    courseID: number,
    contentID: number,
    scormCheckpoint: ScormCheckpoint
  ): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const { data } = await http.post(
        `${contentsPath(courseID, contentID)}/scorm/checkpoints`,
        scormCheckpoint
      );
      return data;
    });
  }

  async saveProgress(
    courseID: number,
    contentID: number,
    speedRate: number
  ): Promise<number> {
    return await wrapErrorHandler<Promise<number>>(async () => {
      const { data } = await http.post(
        `${contentsPath(courseID, contentID)}/progress`,
        speedRate
      );
      return data;
    });
  }

  async generateQuestionnaire(
    courseID: number,
    contentID: number
  ): Promise<number> {
    return await wrapErrorHandler<Promise<number>>(async () => {
      const { data } = await http.post(
        `${contentsPath(courseID, contentID)}/questionnaires`
      );
      return data;
    });
  }

  async getQuestionByPage(
    courseID: number,
    contentID: number,
    questionnaireID: number,
    page: number
  ): Promise<Question> {
    return await wrapErrorHandler<Promise<Question>>(async () => {
      const { data } = await http.get(
        `${contentsPath(
          courseID,
          contentID
        )}/questionnaires/${questionnaireID}/pages/${page}`
      );
      return data;
    });
  }

  async replyQuestionByPage(
    courseID: number,
    contentID: number,
    questionnaireID: number,
    page: number,
    alternativeID: number
  ): Promise<QuestionStatus> {
    return await wrapErrorHandler<Promise<QuestionStatus>>(async () => {
      const { data } = await http.post(
        `${contentsPath(
          courseID,
          contentID
        )}/questionnaires/${questionnaireID}/pages/${page}/alternatives/${alternativeID}`
      );
      return data;
    });
  }

  async replyDescriptiveQuestionByPage(courseID: number, contentID: number, questionnaireID: number, page: number, answer: string): Promise<QuestionStatus> {
    return await wrapErrorHandler<Promise<QuestionStatus>>(async () => {
      const {data} = await http.post(`${contentsPath(courseID, contentID)}/questionnaires/${questionnaireID}/pages/${page}/descriptive`, {answer: answer});
      return data;
    });
  }



  async finishContent(courseID: number,contentID: number): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const { data } = await http.post(
        `${contentsPath(courseID, contentID)}/finish`
      );
      return data;
    });
  }

  async likeRating(ratingID: number): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const { data } = await http.post(`/api/decola/rate/${ratingID}/likes`);
      return data;
    });
  }

  async removeLikeRating(ratingID: number): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const { data } = await http.delete(`/api/decola/rate/${ratingID}/likes`);
      return data;
    });
  }

  async deleteRating(courseID: number, contentID: number, ratingID: number): Promise<void> {
    return await wrapErrorHandler<Promise<void>>(async () => {
      const { data } = await http.delete(`${contentsPath(courseID, contentID)}/rating/${ratingID}`);
      return data;
    });
  }
}

export default new CourseContentRepositoryImpl() as CourseContentRepository;
